import { chartColors } from '@forgd/ui-kit/charts/lib/colors'
// This function takes an object with values like this:
//
// [
//   {
//     "Core Contributors": 0,
//     "Advisors": 0,
//     "Seed Investors": 0,
//   },
//
// And...
// 1. Gets all the keys except for @excludedKeys
// 2. Sorts by the last month's distribution (and then alphabetical)
// 3. Returns an array of colors _in the order of keys in the original object_ to pass in to Unovis

export function generateBarChartMetadata<T extends Record<string, number | string>>(data: T | undefined | null, excludedKeys: Array<keyof T> = []) {
  const fieldColors: Map<keyof T, { color: string, label: string, index: number }> = new Map()

  if (!data) {
    return {
      yAxisAccessors: [],
      colors: [],
      fieldColors,
    }
  }

  const groups: Array<{ field: keyof T, value: number }> = []

  Object.keys(data).forEach((key) => {
    if (!excludedKeys.includes(key) && typeof data[key] === 'number') {
      groups.push({ field: key, value: data[key] })
    }
  })
  const otherFieldKey = '__other'

  const sorted = groups.toSorted((a, b) => {
    const isOtherA = String(a.field) === otherFieldKey
    const isOtherB = String(b.field) === otherFieldKey

    if (isOtherA && !isOtherB) {
      return 1
    }

    if (!isOtherA && isOtherB) {
      return -1
    }

    if (a.value === b.value) {
      return String(a.field).localeCompare(String(b.field))
    }
    return b.value - a.value
  })

  sorted.forEach((group, index) => {
    fieldColors.set(group.field, {
      color: chartColors.tokenDesigner[index % chartColors.tokenDesigner.length]!,
      label: group.field === otherFieldKey ? 'Other' : String(group.field),
      index,
    })
  })

  // in reverse order the bars in the stacked bar will have the same order as the legend
  const inverseSorted = [...sorted].reverse()
  return {
    yAxisAccessors: inverseSorted.map(group => (d: T) => d[group.field] || 0),
    colors: inverseSorted.map(group => fieldColors.get(group.field)?.color ?? ''),
    fieldColors,
  }
}
